// NutriDashboardScreen.jsx V4 "OBS-001: Yesterday tab props threaded to FoodSearchOverlay"
import { useState, useRef, useEffect, Component } from "react";
import { CaretLeft, CaretRight, Funnel, Lightning, Camera, TextT, PencilSimple, AppleLogo, Plus, ArrowClockwise, WarningCircle, Check, X, Trash, Flame, MagnifyingGlass, Flask, Drop, Stack, Cloud, Brain } from "@phosphor-icons/react";
import { T } from "../themes";
import { MEALS, MEAL_ICON, TODAY_ISO, clamp, fmtDuration } from "../constants";
import ComicCard from "../components/ComicCard";
import Btn from "../components/Btn";
import Spinner from "../components/Spinner";
import SectionLabel from "../components/SectionLabel";
import HydrationGauge from "../components/HydrationGauge";
import BurnDownChart from "../components/BurnDownChart";
import SyncBadge from "../components/SyncBadge";
import SecureContextCard from "../components/SecureContextCard";
import FoodSearchOverlay from "../components/FoodSearchOverlay";
import FeatureHint from "../components/FeatureHint";
import AIText from "../components/AIText";
import AppHeader from "../components/AppHeader";

// Camera error boundary — catches camera/stream errors without crashing the dashboard
class CameraErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, msg: '' }; }
  static getDerivedStateFromError(e) { return { hasError: true, msg: e?.message || 'Camera error' }; }
  componentDidCatch(e, info) { if (process.env.NODE_ENV !== "production") console.error('[CameraEB]', e, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,padding:'18px 16px',marginBottom:8,textAlign:'center',borderRadius:18,overflow:'hidden'}}>
          <WarningCircle size={22} weight="bold" color={T.red} style={{marginBottom:8}}/>
          <p style={{color:T.red,fontSize:11,fontFamily:T.fontSans,fontWeight:700,margin:'0 0 4px',letterSpacing:'0.5px',textTransform:'uppercase'}}>Camera fault</p>
          <p style={{color:T.inkMuted,fontSize:9,fontFamily:T.fontSans,fontWeight:500,margin:'0 0 12px'}}>{this.state.msg}</p>
          <button onClick={()=>this.setState({hasError:false,msg:''})}
            style={{background:T.surfaceLight,border:'none',padding:'8px 16px',cursor:'pointer',color:T.inkDark,fontSize:10,fontFamily:T.fontSans,fontWeight:700,borderRadius:12}}>
            Reset scanner
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// NutriLithe Today/Dashboard screen
// Extracted from App.js — receives all state and handlers via props object.
export default function NutriDashboardScreen(props) {
  // Destructure everything from props
  const {
    shell, phone, OfflineBanner, adjustToast, targets, totals, logs, groupedLogs,
    hasTargets, session, persona, headerBg, darkMode, setDarkMode,
    syncing, syncErr, proteinGapAlert, lastProteinLog, hydrationTarget,
    MEAL_COLOR, mealType, setMealType, logMode, setLogMode,
    nlpFood, setNlpFood, nlpCandidates, setNlpCandidates,
    nlpModal, setNlpModal, nlpModalQty, setNlpModalQty, nlpModalError, setNlpModalError,
    nlpHistory, servingPreset, setServingPreset,
    dbSuggestions, setDbSuggestions, dbSearching, dbDebounceRef,
    logLoading, logErr, setLogErr, logOk, setLogOk,
    barcode, setBarcode, barcodeQty, setBarcodeQty, barcodeUnit, setBarcodeUnit,
    barcodeStep, setBarcodeStep, scannedProduct, setScannedProduct,
    manual, setManual, manualUnit, setManualUnit,
    cameraActive, cameraErr, cameraSecureErr, cameraToast,
    cameraRationaleShown, setCameraRationaleShown,
    showCameraRationale, setShowCameraRationale,
    videoReady, videoRef,
    openSlots, setOpenSlots,
    waterMl, syncWater, showCustomWater, setShowCustomWater, customWaterVal, setCustomWaterVal,
    todayWorkouts, hc, postWorkoutNudge, setPostWorkoutNudge,
    showFavs, setShowFavs, savedMeals, savingMeal, saveMeal, deleteSavedMeal,
    showSuggestForm, setShowSuggestForm, suggestForm, setSuggestForm, suggestOk, setSuggestOk, suggestErr, setSuggestErr, submitFoodSuggestion,
    showMacroFilter, setShowMacroFilter, visibleMacros, setVisibleMacros,
    copyLoading, copyOk, copyModal, setCopyModal, copyYestLogs, copySelSlot, copySelItems, setCopySelItems,
    editItem, setEditItem, editForm, setEditForm, editSourceFood, setEditSourceFood, fetchEditSource,
    swipeRef,
    fetchNLP, confirmNlpCandidate, confirmModalLog, insertLog, deleteLog,
    searchFoodsDB, lookupProduct, confirmLog,
    startCameraScanner, stopCamera, handleScan,
    updateMealType, updateLog, saveManual,
    openCopyModal, selectCopySlot, confirmCopyItems, copyAllFromYesterday,
    mealSuggestions, mealSugLoading, mealSugErr, getMealSuggestions,
    dayHistory, logHistory, screen, setScreen,
    viewingDate, isViewingToday, navigateDate, returnToToday,
    searchHistory, fetchSearchHistory, saveSearchQuery,
    hints,
    bodyProjData, bodyProjLoading, onRunBodyProj,
    heroSummary, heroSummaryLoading,
    fetchYesterdayLogs, copyFromYesterday,
  } = props;

  const [showFoodSearch, setShowFoodSearch] = useState(false);
  const [expandedReassignId, setExpandedReassignId] = useState(null);
  const [showWaterModal, setShowWaterModal] = useState(false);

  // Pre-select next unlogged meal when opening food search from the generic "Log food" button
  function openFoodSearch() {
    if (!mealType) {
      const next = MEALS.find(m => !(groupedLogs[m] && groupedLogs[m].length > 0));
      if (next) setMealType(next);
    }
    setShowFoodSearch(true);
  }

  // ── Simple/Macros view switcher (feature-flagged) ───────────────────────
  const SIMPLE_VIEW_ENABLED = true; // set to false to remove entirely
  const [todayView, setTodayView] = useState(() => {
    try { return localStorage.getItem('lithe_today_view') || 'full'; }
    catch { return 'full'; }
  });
  function setView(v) {
    setTodayView(v);
    try { localStorage.setItem('lithe_today_view', v); } catch {}
  }

  // Compute logging streak from dayHistory
  // A day qualifies if: calories logged AND within ±15% of that day's calorie target.
  // Today is neutral — doesn't break the streak, adds to it only if already qualifying.
  const streakData = (() => {
    if (!(dayHistory||[]).length) return { currentStreak: 0, bestStreak: 0 };
    const today = TODAY_ISO();
    // Qualifies: logged calories exist AND within 15% band of stored target.
    // Falls back to current targets.calories for old entries that predate the target field.
    const qualifies = (d) => {
      if (!d || !d.calories) return false;
      const t = d.target || targets.calories;
      if (!t) return d.calories > 0;
      return d.calories >= t * 0.85 && d.calories <= t * 1.15;
    };
    const byDate = {};
    (dayHistory||[]).forEach(d => { byDate[d.date] = d; });
    // Best streak — forward pass from 365 days ago through today
    let best = 0, runLen = 0;
    for (let i = 365; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0,10);
      if (qualifies(byDate[iso])) { runLen++; best = Math.max(best, runLen); }
      else { runLen = 0; }
    }
    // Current streak — backward pass from yesterday; today adds only if qualifying
    let cur = 0;
    for (let i = 1; i <= 365; i++) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0,10);
      if (qualifies(byDate[iso])) cur++; else break;
    }
    if (qualifies(byDate[today])) cur++;
    return { currentStreak: cur, bestStreak: best };
  })();

  const calPct   = clamp(targets.calories>0?totals.calories/targets.calories:0,0,1);
  const calColor = calPct>1?T.red:calPct>0.85?T.orange:T.accent;

  // V26 HOTFIX: Serving multiplier state for NLP and barcode confirm screens
  const [nlpServings, setNlpServings] = useState("1");
  const [barcodeServings, setBarcodeServings] = useState("1");

  // Cooking variant helpers
  const getVariantLabel = (name) => {
    if (/raw/i.test(name)) return 'Raw';
    if (/cooked/i.test(name)) return 'Cooked';
    if (/grilled/i.test(name)) return 'Grilled';
    if (/baked/i.test(name)) return 'Baked';
    if (/boiled/i.test(name)) return 'Boiled';
    if (/dried|dry/i.test(name)) return 'Dried';
    if (/fried/i.test(name)) return 'Fried';
    if (/smoked/i.test(name)) return 'Smoked';
    if (/steamed/i.test(name)) return 'Steamed';
    if (/roasted/i.test(name)) return 'Roasted';
    return 'Standard';
  };

  // V25 FIX 12: Macro view toggle (bars vs ring) — persisted in localStorage
  const [macroView, setMacroView] = useState(() => {
    try { return localStorage.getItem("macro_view_pref") || "bars"; } catch(_) { return "bars"; }
  });
  function toggleMacroView() {
    const next = macroView === "bars" ? "ring" : "bars";
    setMacroView(next);
    try { localStorage.setItem("macro_view_pref", next); } catch(_) {}
  }

  // V28: Show food search hint on first load after onboarding
  useEffect(() => {
    if (hasTargets && logs.length === 0) {
      hints.showHint("food_search");
    }
  }, [hasTargets]); // eslint-disable-line react-hooks/exhaustive-deps

  // V28: Hydration hint removed — onboarding noise

  return (
    <div style={shell}><div style={phone}>

      {OfflineBanner}
      {/* V26 HOTFIX: Persistent banner when targets are zero after load */}
      {!hasTargets && (
        <button onClick={()=>setScreen&&setScreen("settings")} style={{
          width:"100%",background:T.yellow,border:"none",padding:"10px 16px",
          cursor:"pointer",display:"flex",alignItems:"center",gap:8,boxSizing:"border-box",
        }}>
          <WarningCircle size={14} weight="bold" color={T.ink}/>
          <span style={{color:T.ink,fontSize:11,fontFamily:T.fontSans,fontWeight:700,letterSpacing:"0.5px"}}>
            Your nutrition targets need to be set — tap here
          </span>
        </button>
      )}
      {adjustToast&&(
        <div style={{background:T.accent,border:T.border,padding:"10px 16px",
          borderRadius:T.radiusSm,display:"flex",alignItems:"center",gap:8}}>
          <Lightning size={13} weight="bold" color={T.ink}/>
          <p style={{color:T.ink,fontSize:10,fontFamily:T.fontSans,fontWeight:"bold",margin:0}}>
            {adjustToast}
          </p>
        </div>
      )}
      {/* ── Header ─────────────────────────────────────────────────── */}
      <AppHeader
        title="Today"
        subtitle={new Date(viewingDate+"T00:00:00").toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})}
        persona={persona}
        darkMode={darkMode}
        onToggleDarkMode={()=>setDarkMode(d=>!d)}
        currentApp="nutri"
        onAvatarPress={()=>setScreen&&setScreen("settings")}
        username={session?.user?.email}
      />

      {/* ── Tabs ───────────────────────────────────────────────────── */}
      {SIMPLE_VIEW_ENABLED && (
        <div style={{background:T.surfaceMid,margin:"0 16px 12px",borderRadius:T.radiusPill,padding:3,display:"flex",gap:2}}>
          {["full","simple","macros"].map(v=>(
            <button key={v} onClick={()=>setView(v)} style={{
              flex:1,padding:"8px 0",textAlign:"center",cursor:"pointer",
              background:todayView===v?T.accent:"transparent",
              border:"none",borderRadius:T.radiusPill,
              color:todayView===v?"#fff":T.inkMuted,
              fontSize:11,fontWeight:700,fontFamily:T.fontSans,
            }}>{v.charAt(0).toUpperCase()+v.slice(1)}</button>
          ))}
        </div>
      )}

      {/* ── Date navigation ────────────────────────────────────────── */}
      <div style={{backgroundColor:T.surfaceMid,borderBottomWidth:1,borderBottomColor:T.cardBorder,borderBottomStyle:"solid"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",background:T.surfaceMid,borderBottom:`1px solid ${T.cardBorder}`}}>
        <button onClick={()=>navigateDate(-1)} style={{
          width:28,height:28,borderRadius:8,background:T.card,border:`1px solid ${T.cardBorder}`,
          cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
        }}><CaretLeft size={14} weight="bold" color={T.inkMuted}/></button>
        <button onClick={returnToToday} style={{background:"transparent",border:"none",cursor:"pointer",padding:"2px 8px",display:"flex",flexDirection:"column",alignItems:"center"}}>
          <span style={{fontSize:15,fontWeight:800,color:T.inkDark,fontFamily:T.fontDisplay}}>
            {isViewingToday ? "Today" : new Date(viewingDate+"T00:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"})}
          </span>
          <SyncBadge syncing={syncing} error={syncErr}/>
        </button>
        <button onClick={()=>navigateDate(+1)} disabled={isViewingToday} style={{
          width:28,height:28,borderRadius:8,background:T.card,border:`1px solid ${T.cardBorder}`,
          cursor:isViewingToday?"not-allowed":"pointer",opacity:isViewingToday?0.3:1,
          display:"flex",alignItems:"center",justifyContent:"center",
        }}><CaretRight size={14} weight="bold" color={T.inkMuted}/></button>
      </div>
      </div>
      {!isViewingToday && (
        <div style={{
          background:T.accent,padding:"6px 12px",margin:"0 17px 4px",
          display:"flex",alignItems:"center",justifyContent:"space-between",borderRadius:8,
        }}>
          <span style={{color:T.ink,fontSize:10,fontWeight:700,fontFamily:T.fontSans,letterSpacing:"0.5px",textTransform:"uppercase"}}>
            Viewing {new Date(viewingDate+"T00:00:00").toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"short"})}
          </span>
          <button onClick={returnToToday} style={{
            background:T.ink,border:"none",cursor:"pointer",color:T.card,
            fontSize:9,fontWeight:700,fontFamily:T.fontSans,padding:"3px 10px",
            borderRadius:8,
          }}>Return to today</button>
        </div>
      )}

      {/* ── SIMPLE VIEW ─────────────────────────────────────────────────── */}
      {SIMPLE_VIEW_ENABLED && todayView === 'simple' && (()=>{
        const MACRO_DISPLAY={
          calories:{key:"calories",label:"K",color:T.macroCalories,unit:""},
          protein:{key:"protein",label:"P",color:T.macroProtein,unit:"g"},
          carbs:{key:"carbs",label:"C",color:T.macroCarbs,unit:"g"},
          fat:{key:"fat",label:"F",color:T.macroFat,unit:"g"},
          fibre:{key:"fibre",label:"Fi",color:T.macroFibre,unit:"g"},
          sugar:{key:"sugar",label:"Su",color:T.macroSugar,unit:"g"},
          sodium:{key:"sodium",label:"Na",color:T.macroSodium,unit:"mg"},
        };
        const macroColumns=(visibleMacros||["calories","protein","carbs"]).filter(f=>MACRO_DISPLAY[f]).map(f=>MACRO_DISPLAY[f]);
        return(
        <div style={{background:T.bg,display:"flex",flexDirection:"column",flex:"1 1 auto",minHeight:0,height:"calc(100dvh - 180px)"}}>
          <div style={{padding:"0 16px 8px",flexShrink:0}}>
            <button onClick={openFoodSearch} style={{
              width:"100%",background:T.actionPrimary,border:"none",borderRadius:T.radius,padding:15,cursor:"pointer",
              color:"#ffffff",fontSize:15,fontWeight:800,fontFamily:T.fontDisplay,
              display:"flex",alignItems:"center",justifyContent:"center",gap:8,
            }}><Plus size={16} weight="bold" color="#ffffff"/>Log food</button>
            {hints.isVisible("food_search") && (
              <FeatureHint id="food_search" text="Tap here to log your first meal. Search by name, scan a barcode, or browse branded foods." onDismiss={() => hints.dismissHint("food_search")} />
            )}
          </div>
          <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"0 16px 24px"}}>
            {MEALS.map(mt=>{
              const items=(groupedLogs[mt]||[]);
              if(!items.length) return null;
              const mc=MEAL_COLOR[mt]||T.primary;
              const mealKcal=items.reduce((s,l)=>s+(parseFloat(l.calories)||0),0);
              return(
                <div key={mt} style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,overflow:"hidden",marginBottom:8}}>
                  {/* Dark header — same as Full view */}
                  <div style={{background:T.progCardBg,padding:"12px 16px",display:"flex",alignItems:"center",gap:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:0}}>
                      <div style={{width:10,height:10,borderRadius:5,background:mc,flexShrink:0}}/>
                      <span style={{fontSize:14,fontWeight:700,color:T.tileText,fontFamily:T.fontDisplay}}>{mt}</span>
                      <span style={{fontSize:10,fontWeight:600,color:"rgba(255,255,255,0.60)",background:"rgba(255,255,255,0.12)",borderRadius:8,padding:"2px 8px",marginLeft:6,fontFamily:T.fontSans}}>
                        {items.length} item{items.length!==1?"s":""}
                      </span>
                    </div>
                    <div style={{fontFamily:T.fontDisplay,fontSize:20,fontWeight:800,color:T.tileText,lineHeight:1,flexShrink:0}}>{Math.round(mealKcal)}</div>
                    <div onClick={e=>{e.stopPropagation();setMealType(mt);setShowFoodSearch(true);}} style={{width:28,height:28,borderRadius:14,background:"rgba(255,255,255,0.12)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,marginLeft:4}}>
                      <Plus size={14} weight="bold" color={T.tileText}/>
                    </div>
                  </div>
                  {/* Food item rows */}
                  <div style={{padding:"0 16px"}}>
                    {items.map(log=>(
                      <div key={log.id} style={{display:"flex",alignItems:"center",padding:"12px 0",borderTop:`1px solid ${T.cardBorder}`}}>
                        <div style={{flex:1,minWidth:0}}>
                          <p style={{color:T.inkDark,fontSize:14,fontWeight:600,fontFamily:T.fontSans,margin:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{log.name}</p>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                          {macroColumns.map(m=>(
                            <div key={m.key} style={{textAlign:"center",width:38,overflow:"hidden"}}>
                              <div style={{fontSize:12,fontWeight:800,color:m.color,fontFamily:T.fontDisplay}}>{Math.round(log[m.key]||0)}{m.unit}</div>
                              <div style={{fontSize:9,fontWeight:600,color:T.inkMuted,fontFamily:T.fontSans}}>{m.label}</div>
                            </div>
                          ))}
                          <button onClick={()=>deleteLog(log.id)} style={{background:"none",border:"none",cursor:"pointer",padding:4,color:T.inkFaint,display:"flex"}}><X size={12} weight="bold"/></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {Object.keys(groupedLogs).length===0&&(
              <div style={{textAlign:"center",padding:"32px 16px",background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,overflow:"hidden"}}>
                <p style={{fontSize:20,fontWeight:800,margin:"0 0 6px",color:T.inkDark,fontFamily:T.fontDisplay}}>Nothing logged yet</p>
                <p style={{fontSize:12,margin:0,color:T.inkMuted,fontFamily:T.fontSans,fontWeight:500}}>Tap Log food to get started</p>
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* ── MACROS VIEW ──────────────────────────────────────────────────── */}
      {SIMPLE_VIEW_ENABLED && todayView === 'macros' && (
        <div style={{background:T.bg,display:"flex",flexDirection:"column",flex:"1 1 auto",minHeight:0,height:"calc(100dvh - 180px)"}}>
          <div style={{padding:"0 16px 8px",flexShrink:0}}>
            <button onClick={openFoodSearch} style={{
              width:"100%",background:T.actionPrimary,border:"none",borderRadius:T.radius,padding:15,cursor:"pointer",
              color:"#ffffff",fontSize:15,fontWeight:800,fontFamily:T.fontDisplay,
              display:"flex",alignItems:"center",justifyContent:"center",gap:8,
            }}><Plus size={16} weight="bold" color="#ffffff"/>Log food</button>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"0 16px 24px"}}>
            {/* Calorie hero summary */}
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{fontFamily:T.fontDisplay,fontSize:48,fontWeight:900,color:T.inkDark,lineHeight:1}}>{Math.round(totals.calories||0)}</div>
              <div style={{fontFamily:T.fontSans,fontSize:13,color:T.inkMuted,marginTop:4}}>of {targets.calories||0} kcal</div>
              {targets.calories>0&&<div style={{fontFamily:T.fontSans,fontSize:12,color:T.actionPrimary,fontWeight:700,marginTop:2}}>{Math.round(((totals.calories||0)/(targets.calories||1))*100)}% eaten today</div>}
            </div>
            <div style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,padding:"16px 16px 16px",overflow:"hidden"}}>
              <span style={{fontSize:13,fontWeight:600,color:T.inkDark,fontFamily:T.fontSans,display:"block",marginBottom:14}}>Today's macros</span>
              {[
                {key:"calories",label:"Calories",val:totals.calories,target:targets.calories,color:T.macroCalories,unit:"",isCal:true},
                {key:"protein",label:"Protein",val:totals.protein,target:targets.protein,color:T.macroProtein,unit:"g"},
                {key:"carbs",label:"Carbs",val:totals.carbs,target:targets.carbs,color:T.macroCarbs,unit:"g"},
                {key:"fat",label:"Fat",val:totals.fat,target:targets.fat,color:T.macroFat,unit:"g"},
                {key:"fibre",label:"Fibre",val:totals.fibre,target:targets.fibre,color:T.macroFibre,unit:"g"},
                {key:"sugar",label:"Sugar",val:totals.sugar,target:targets.sugar,color:T.macroSugar,unit:"g"},
                {key:"sodium",label:"Sodium",val:totals.sodium,target:2300,color:T.macroSodium,unit:"mg"},
              ].filter(m=>visibleMacros.includes(m.key)).map(m=>{
                const pct=Math.min(100,Math.round(((m.val||0)/(m.target||1))*100));
                const over=(m.val||0)>(m.target||0);
                return(
                  <div key={m.key} style={{marginBottom:14}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
                      <span style={{fontSize:13,fontWeight:600,color:T.inkDark,fontFamily:T.fontSans}}>{m.label}</span>
                      <div>
                        <span style={{fontFamily:T.fontDisplay,fontSize:m.isCal?16:14,fontWeight:800,color:over?T.red:m.color}}>{Math.round(m.val||0)}{m.unit}</span>
                        <span style={{fontSize:12,color:T.inkMuted,fontFamily:T.fontSans,fontWeight:500}}> / {m.target}{m.unit}</span>
                      </div>
                    </div>
                    {pct>0&&<span style={{fontSize:10,color:T.inkMuted,fontWeight:500,fontFamily:T.fontSans,float:"right",marginBottom:2}}>{pct}%</span>}
                    <div style={{height:m.isCal?8:6,borderRadius:3,background:T.surfaceLight,overflow:"hidden",marginTop:m.isCal?6:4,clear:"both"}}>
                      <div style={{height:"100%",borderRadius:3,background:over?T.red:m.color,width:`${pct}%`,transition:"width 0.3s ease"}}/>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Quick actions ──────────────────────────────────────── */}
            <div style={{display:"flex",gap:8,marginTop:14}}>
              {[
                {icon:<Plus size={14} weight="bold" color={T.actionPrimary}/>, label:"Log food", action:openFoodSearch},
                {icon:<Drop size={14} weight="fill" color={T.hydrationBlue}/>, label:"+250ml water", action:()=>syncWater(+250)},
              ].map(({icon,label,action})=>(
                <button key={label} onClick={action} style={{
                  flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                  padding:"10px 12px",background:T.card,border:`1px solid ${T.cardBorder}`,
                  borderRadius:T.radiusSm,cursor:"pointer",
                  fontSize:12,fontWeight:700,fontFamily:T.fontSans,color:T.inkDark,
                }}>
                  {icon}{label}
                </button>
              ))}
            </div>

            {/* ── Today's meals summary ──────────────────────────────── */}
            {(()=>{
              const mealRows=MEALS.map(mt=>({mt,items:groupedLogs[mt]||[]})).filter(({items})=>items.length>0);
              if(!mealRows.length) return null;
              return(
                <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,overflow:"hidden",marginTop:14}}>
                  <div style={{padding:"10px 14px",borderBottom:`1px solid ${T.cardBorder}`}}>
                    <span style={{fontSize:10,fontWeight:700,color:T.inkMuted,fontFamily:T.fontSans,letterSpacing:"0.5px"}}>Today's meals</span>
                  </div>
                  {mealRows.map(({mt,items},idx)=>{
                    const kcal=Math.round(items.reduce((s,l)=>s+(parseFloat(l.calories)||0),0));
                    const mc=MEAL_COLOR[mt]||T.accent;
                    return(
                      <button key={mt} onClick={()=>{setMealType(mt);setTodayView('full');}} style={{
                        width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",
                        padding:"10px 14px",background:"none",border:"none",cursor:"pointer",
                        borderTop:idx>0?`1px solid ${T.cardBorder}`:"none",
                      }}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{width:8,height:8,borderRadius:4,background:mc,flexShrink:0}}/>
                          <span style={{fontSize:13,fontWeight:600,fontFamily:T.fontSans,color:T.inkMuted}}>{mt}</span>
                          <span style={{fontSize:11,fontWeight:500,fontFamily:T.fontSans,color:T.inkFaint}}>{items.length} item{items.length!==1?"s":""}</span>
                        </div>
                        <span style={{fontSize:13,fontWeight:700,fontFamily:T.fontDisplay,color:T.inkDark}}>{kcal} kcal</span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Content area — white/dark bg depending on persona, NOT header bg */}
      <div style={{background:T.bg,padding:"0 16px",paddingBottom:130,display:(!SIMPLE_VIEW_ENABLED||todayView==='full')?undefined:"none"}}>

        {/* ── Workout Credit Cards ──────────────────────────────────────── */}
        {todayWorkouts.filter(w=>w.calories_burned>0).map(w=>(
          <div key={w.id} onClick={()=>{
            const isNative = typeof window !== "undefined" &&
              (window.location.protocol === "capacitor:" || !!(window.Capacitor?.isNativePlatform?.()));
            if (!isNative) window.location.href = "/workout";
          }} style={{
            background:T.ink,border:`1px solid ${T.cardBorder}`,
            padding:"9px 13px",marginBottom:8,cursor:"pointer",
            display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,
            borderRadius:18,
          }}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <Lightning size={14} weight="bold" color={T.yellow}/>
              <div>
                <p style={{color:T.yellow,fontSize:10,fontFamily:T.fontSans,fontWeight:700,
                  margin:0,letterSpacing:"0.5px",textTransform:"uppercase"}}>
                  {w.name}
                </p>
                <p style={{color:T.textDim,fontSize:8,fontFamily:T.fontSans,fontWeight:600,margin:0}}>
                  {fmtDuration(w.duration||0)} · Workout credit
                </p>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{textAlign:"right",flexShrink:0}}>
                <p style={{color:T.yellow,fontSize:15,fontWeight:800,fontFamily:T.fontDisplay,margin:0}}>
                  ~{w.calories_burned}
                </p>
                <p style={{color:T.textDim,fontSize:9,fontFamily:T.fontSans,fontWeight:600,margin:0}}>kcal burned</p>
              </div>
              <CaretRight size={14} weight="bold" color={T.yellow}/>
            </div>
          </div>
        ))}

        {/* ── Health Connect Activity Card (Part 1) ────────────────────── */}
        {hc.isNative && hc.isAvailable && (hc.healthData?.steps != null || hc.healthData?.activeCalories != null) && (
          <div style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,padding:"12px 16px",marginBottom:8,overflow:"hidden"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <span style={{fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",textTransform:"uppercase",fontFamily:T.fontSans}}>Activity today</span>
              <span style={{fontSize:9,color:T.inkMuted,fontFamily:T.fontSans,fontWeight:500}}>Health Connect</span>
            </div>
            <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
              {hc.healthData?.steps != null && (
                <div style={{textAlign:"center"}}>
                  <p style={{margin:0,fontSize:18,fontFamily:T.fontDisplay,fontWeight:800,color:T.inkDark}}>{hc.healthData.steps.toLocaleString()}</p>
                  <p style={{margin:0,fontSize:9,color:T.inkMuted,fontFamily:T.fontSans,fontWeight:500}}>Steps</p>
                </div>
              )}
              {hc.healthData?.activeCalories != null && (
                <div style={{textAlign:"center"}}>
                  <p style={{margin:0,fontSize:18,fontFamily:T.fontDisplay,fontWeight:800,color:T.accent}}>{Math.round(hc.healthData.activeCalories)}</p>
                  <p style={{margin:0,fontSize:9,color:T.inkMuted,fontFamily:T.fontSans,fontWeight:500}}>Active kcal</p>
                </div>
              )}
              {hc.healthData?.heartRate != null && (
                <div style={{textAlign:"center"}}>
                  <p style={{margin:0,fontSize:18,fontFamily:T.fontDisplay,fontWeight:800,color:T.red||T.danger||"#e24b4a"}}>{Math.round(hc.healthData.heartRate)}</p>
                  <p style={{margin:0,fontSize:9,color:T.inkMuted,fontFamily:T.fontSans,fontWeight:500}}>BPM</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Nutrition Card (full-width) ─────────────────────────── */}
        {hasTargets&&(()=>{
          const calLeft=Math.round(targets.calories-totals.calories);
          const calOver=calLeft<0;
          const ratio=Math.min((totals.calories||0)/(targets.calories||1),1);
          const pctUsed=Math.round(ratio*100);
          const macroRows=[
            {key:"protein",label:"Protein",color:T.macroProtein},
            {key:"carbs",label:"Carbs",color:T.macroCarbs},
            {key:"fat",label:"Fat",color:T.macroFat},
            {key:"fibre",label:"Fibre",color:T.macroFibre},
            {key:"sugar",label:"Sugar",color:T.macroSugar},
            {key:"sodium",label:"Sodium",color:T.macroSodium},
          ];
          const shownMacros=macroRows.filter(m=>visibleMacros.includes(m.key)).slice(0,4);
          return(
            <div style={{background:T.tileBg,borderRadius:18,overflow:"hidden",marginBottom:8}}>
              {/* Banner strip */}
              <div style={{background:T.progBannerBg,padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <span style={{fontSize:9,fontWeight:700,color:T.progEyebrow,letterSpacing:1.5}}>Today's nutrition</span>
                <button onClick={()=>setShowMacroFilter(p=>!p)} style={{display:"flex",alignItems:"center",gap:4,background:"rgba(255,255,255,0.08)",borderRadius:8,padding:"4px 8px",border:"none",cursor:"pointer"}}>
                  <Funnel size={10} weight={showMacroFilter?"fill":"regular"} color="rgba(255,255,255,0.50)"/>
                  <span style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.50)"}}>Filter</span>
                </button>
              </div>
              {/* Body */}
              <div style={{padding:"14px 16px 16px"}}>
                {/* Top section — calories + ring */}
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:14}}>
                  <div>
                    {(()=>{
                      const isCritical = T.tileBg === "#2a1a1a";
                      const isMotivational = T.tileBg === "#0f3020";
                      const calorieLabel = isCritical ? null : isMotivational ? "Available today" : "Calories remaining";
                      return calorieLabel ? (
                        <div style={{fontSize:11,fontWeight:600,color:T.tileMuted,fontFamily:T.fontSans,marginBottom:4}}>{calorieLabel}</div>
                      ) : null;
                    })()}
                    <div style={{fontFamily:T.fontDisplay,fontSize:56,fontWeight:900,color:T.tileText,lineHeight:1,textShadow:"0 2px 8px rgba(0,0,0,0.3)"}}>
                      {calLeft>=0?calLeft.toLocaleString():Math.abs(calLeft).toLocaleString()}
                    </div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",fontFamily:T.fontSans,marginTop:5}}>
                      {Math.round(totals.calories||0)} eaten · {targets.calories||0} target
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                    <div style={{position:"relative",width:80,height:80}}>
                      <svg width={80} height={80} viewBox="0 0 80 80" style={{display:"block"}}>
                        <circle cx={40} cy={40} r={33} fill="none"
                          stroke={pctUsed===0 ? T.cardBorder : "rgba(255,255,255,0.08)"} strokeWidth={6}
                          strokeDasharray={pctUsed===0 ? "207.3 0" : "155.5 51.8"} strokeLinecap="round"
                          transform="rotate(-225, 40, 40)"/>
                        {pctUsed > 0 && (
                          <circle cx={40} cy={40} r={33} fill="none" stroke={T.actionPrimary} strokeWidth={6}
                            strokeDasharray={`${Math.min(ratio,1)*155.5} ${207.3-Math.min(ratio,1)*155.5}`} strokeLinecap="round"
                            transform="rotate(-225, 40, 40)" style={{transition:"stroke-dasharray 0.5s ease"}}/>
                        )}
                      </svg>
                      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                        {pctUsed === 0 ? (
                          <div style={{fontSize:18,fontWeight:800,color:T.inkFaint,lineHeight:1,fontFamily:T.fontDisplay}}>—</div>
                        ) : (
                          <>
                            <div style={{fontSize:18,fontWeight:800,color:T.tileText,lineHeight:1,fontFamily:T.fontDisplay}}>{pctUsed}%</div>
                            <div style={{fontSize:10,color:T.inkFaint,fontWeight:600,fontFamily:T.fontSans}}>eaten</div>
                          </>
                        )}
                      </div>
                    </div>
                    <div style={{fontSize:9,color:"rgba(255,255,255,0.35)",fontFamily:T.fontSans,textAlign:"center",marginTop:2}}>of daily target</div>
                  </div>
                </div>
                {/* Filter panel */}
                {showMacroFilter&&(
                  <div style={{background:"rgba(255,255,255,0.05)",borderRadius:12,padding:"10px 12px",marginBottom:12}}>
                    <div style={{fontSize:9,fontWeight:700,color:T.tileMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Show macros</div>
                    <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                      {[{key:"calories",label:"Calories"},...macroRows].map(m=>{
                        const active=visibleMacros.includes(m.key);
                        return(
                          <button key={m.key} onClick={()=>setVisibleMacros(prev=>active?prev.filter(k=>k!==m.key):[...prev,m.key])}
                            style={{fontSize:9,fontWeight:700,fontFamily:T.fontSans,padding:"4px 10px",borderRadius:20,cursor:"pointer",
                              border:active?"1px solid rgba(255,255,255,0.25)":"1px solid rgba(255,255,255,0.10)",
                              background:active?"rgba(255,255,255,0.15)":"transparent",
                              color:active?"#fff":"rgba(255,255,255,0.25)",textTransform:"uppercase",letterSpacing:"0.3px"}}>
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Divider */}
                <div style={{height:1,background:"rgba(255,255,255,0.08)",marginTop:12,marginBottom:12,width:"100%"}}/>
                {/* Hero AI summary */}
                <style>{`@keyframes np-pulse{0%,100%{opacity:0.4}50%{opacity:0.9}}`}</style>
                {heroSummaryLoading && !heroSummary && (
                  <div style={{
                    height:14, borderRadius:7,
                    background:"rgba(255,255,255,0.06)",
                    marginBottom:10, width:"72%",
                    animation:"np-pulse 1.5s ease-in-out infinite",
                  }}/>
                )}
                {heroSummary && (
                  <div style={{
                    fontFamily:   T.fontSans,
                    fontSize:     12,
                    fontWeight:   500,
                    color:        "rgba(255,255,255,0.55)",
                    fontStyle:    "italic",
                    lineHeight:   1.45,
                    marginBottom: 10,
                    letterSpacing:"0.1px",
                  }}>
                    {heroSummary}
                  </div>
                )}
                {/* Macro mini-cards grid */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {shownMacros.map(m=>{
                    const curr=totals[m.key]||0;
                    const tgt=targets[m.key]||1;
                    const remain=Math.max(Math.round(tgt-curr),0);
                    const unit=m.key==="sodium"?"mg":"g";
                    const isEmpty=curr===0;
                    // FIX 1: almost-there sub-label
                    const eaten=tgt-remain;
                    const pctEaten=tgt>0?eaten/tgt:0;
                    const subLabel=pctEaten>=1?"target hit!":pctEaten>=0.9?"almost there":`left of ${tgt}${unit}`;
                    const subColor=pctEaten>=0.9?T.nutriGreen:T.inkFaint;
                    // FIX 3: progress bar uses eaten/target
                    const pctBar=Math.min(100,Math.round((eaten/tgt)*100));
                    return(
                      <div key={m.key} style={{background:"rgba(255,255,255,0.05)",borderRadius:12,padding:"10px 12px"}}>
                        <div style={{fontSize:9,fontWeight:700,color:T.tileMuted,fontFamily:T.fontSans,letterSpacing:0.5,marginBottom:5}}>{m.label}</div>
                        <div style={{fontFamily:T.fontDisplay,fontSize:22,fontWeight:800,color:m.color,lineHeight:1,marginBottom:2}}>
                          {isEmpty ? `${tgt}${unit}` : `${remain}${unit}`}
                        </div>
                        <div style={{fontSize:11,color:isEmpty?T.inkFaint:subColor,fontWeight:500,fontFamily:T.fontSans}}>
                          {isEmpty ? "daily goal" : subLabel}
                        </div>
                        {!isEmpty && (
                          <div style={{height:3,background:"rgba(255,255,255,0.08)",borderRadius:3,marginTop:6,overflow:"hidden"}}>
                            <div style={{height:3,borderRadius:3,background:m.color,width:`${pctBar}%`,minWidth:2,transition:"width 0.3s ease"}}/>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Today: Logging + Water only ────────────────────────────────────── */}
        <>

          {/* ── Streak Widget ─────────────────────────────────────────────── */}
          <div style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,padding:"14px 16px",marginBottom:8,display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:24,lineHeight:1,flexShrink:0}}>🔥</span>
            {streakData.currentStreak === 0 ? (
              <div>
                <p style={{margin:0,fontFamily:T.fontDisplay,fontSize:14,fontWeight:700,color:T.accent}}>Start your streak — log food today</p>
                <p style={{margin:"2px 0 0",fontFamily:T.fontSans,fontSize:11,fontWeight:500,color:T.inkMuted}}>Log every day to build your streak</p>
              </div>
            ) : (
              <>
                <div style={{display:"flex",flexDirection:"column",gap:1}}>
                  <span style={{color:T.inkDark,fontSize:28,fontWeight:800,fontFamily:T.fontDisplay,lineHeight:1}}>{streakData.currentStreak}</span>
                  <span style={{color:T.inkMuted,fontSize:12,fontWeight:500,fontFamily:T.fontSans}}>day streak</span>
                </div>
                <div style={{marginLeft:"auto",textAlign:"right"}}>
                  {streakData.currentStreak === streakData.bestStreak && streakData.currentStreak > 1 ? (
                    <span style={{color:T.tileAccent,fontSize:12,fontWeight:700,fontFamily:T.fontSans}}>Personal best!</span>
                  ) : (
                    <span style={{color:T.accent,fontSize:12,fontWeight:700,fontFamily:T.fontSans}}>Keep it going!</span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* V27 FIX 8: Post-workout nutrition nudge */}
          {postWorkoutNudge && (
            <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderLeft:`4px solid ${T.accent}`,padding:"12px 16px",marginBottom:8,borderRadius:18,position:"relative",overflow:"hidden"}}>
              <button onClick={()=>setPostWorkoutNudge(null)} style={{position:"absolute",top:8,right:8,background:"none",border:"none",cursor:"pointer",color:T.inkMuted,padding:2}}><X size={14} weight="bold"/></button>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <span style={{color:T.accent,fontSize:10,fontFamily:T.fontSans,fontWeight:700,letterSpacing:"1px",textTransform:"uppercase"}}>Post-workout fuel</span>
              </div>
              <p style={{color:T.inkDark,fontSize:11,fontFamily:T.fontSans,fontWeight:500,margin:0,lineHeight:1.6}}><AIText text={postWorkoutNudge.message} persona={persona}/></p>
            </div>
          )}

          {/* ── Meal Timing Pills ─────────────────────────────────────── */}
          <div style={{display:"flex",gap:6,flexWrap:"wrap",padding:"0 0 10px"}}>
            {/* All pill — deselects active meal filter */}
            <button onClick={()=>setMealType(null)} style={{
              display:"flex",alignItems:"center",justifyContent:"center",
              padding:"7px 13px",borderRadius:20,
              background:!mealType ? T.accent : `${T.accent}2E`,
              border:!mealType ? "none" : `1.5px solid ${T.accent}66`,
              color:!mealType ? "#fff" : `${T.accent}B3`,
              cursor:"pointer",fontSize:11,fontWeight:!mealType?700:600,fontFamily:T.fontSans,
              whiteSpace:"nowrap",
            }}>All</button>
            {MEALS.map(mt=>{
              const filled = !!(groupedLogs[mt] && groupedLogs[mt].length > 0);
              const mc = MEAL_COLOR[mt]||T.accent;
              const isActive = mealType===mt;
              // Ghost state: tinted bg + border when not filled
              const pillBg = filled ? mc : (isActive ? mc : `${mc}2E`);
              const pillBorder = filled ? "none" : `1.5px solid ${mc}66`;
              const pillColor = filled
                ? (({Breakfast:T.mealBreakfastText,Lunch:T.mealLunchText,Dinner:T.mealDinnerText,Snack:T.mealSnackText})[mt]||"#fff")
                : (isActive ? "#fff" : `${mc}B3`);
              return (
                <button key={mt} onClick={()=>setMealType(mt)} style={{
                  display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                  padding:"7px 13px",borderRadius:20,
                  border:pillBorder,
                  background:pillBg,
                  color:pillColor,
                  cursor:"pointer",fontSize:11,fontWeight:filled||isActive?700:600,fontFamily:T.fontSans,
                  whiteSpace:"nowrap",
                }}>
                  {mt}
                  {/* OBS-002: dot indicator when meal has logged entries */}
                  <span style={{
                    display:"block",width:4,height:4,borderRadius:2,
                    background:"currentColor",marginTop:2,
                    opacity:filled?0.75:0,
                    transition:"opacity 0.2s",
                    flexShrink:0,
                  }}/>
                </button>
              );
            })}
          </div>

          {/* Hydration moved to sticky bar above bottom nav */}




          {/* ── Log Food Button — opens full-screen overlay ──────────── */}
          <button onClick={openFoodSearch} style={{
            width:"100%",padding:15,background:T.actionPrimary,border:"none",
            boxShadow:`0 6px 24px ${T.actionPrimary}55`,cursor:"pointer",display:"flex",alignItems:"center",
            justifyContent:"center",gap:8,marginBottom:8,borderRadius:T.radius,
          }}>
            <Plus size={16} weight="bold" color="#ffffff"/>
            <span style={{color:"#ffffff",fontSize:15,fontFamily:T.fontDisplay,fontWeight:800}}>Log food</span>
          </button>
          {/* V28: nlpModal confirm screen — dynamic macro calculation */}
          {nlpModal && (()=>{
            const isBranded = nlpModal.source==="branded";
            const baseGrams = parseFloat(nlpModal.grams)||100;
            const qty = parseFloat(nlpModalQty)||100;
            const servMult = parseFloat(nlpServings)||1;
            const totalGrams = qty * servMult;
            const s = totalGrams / 100;
            const noMacros = nlpModal.kcal100===0 && nlpModal.p100===0 && nlpModal.f100===0;
            const calcKcal = Math.round(nlpModal.kcal100 * s);
            const calcP = (nlpModal.p100 * s).toFixed(1);
            const calcC = (nlpModal.c100 * s).toFixed(1);
            const calcF = (nlpModal.f100 * s).toFixed(1);

            // Category-based serving size pills
            const CATEGORY_SERVINGS = {
              'Chicken':[{l:'Small breast',g:120},{l:'Med breast',g:150},{l:'Large breast',g:200}],
              'Fish':[{l:'Small fillet',g:100},{l:'Med fillet',g:150},{l:'Large fillet',g:200}],
              'Beef':[{l:'Small',g:100},{l:'Medium',g:150},{l:'Large',g:200}],
              'Lamb':[{l:'Small',g:100},{l:'Medium',g:150},{l:'Large',g:200}],
              'Pork':[{l:'Small',g:100},{l:'Medium',g:150},{l:'Large',g:200}],
              'Eggs':[{l:'Small egg',g:48},{l:'Medium egg',g:58},{l:'Large egg',g:68},{l:'2 eggs',g:116},{l:'3 eggs',g:174}],
              'Vegetables':[{l:'Small',g:80},{l:'Medium',g:120},{l:'Large',g:200}],
              'Fruit':[{l:'Small',g:100},{l:'Medium',g:150},{l:'Large',g:200}],
              'Bread':[{l:'1 slice',g:35},{l:'2 slices',g:70}],
              'Pasta':[{l:'Small (dry)',g:60},{l:'Med (dry)',g:80},{l:'Large (dry)',g:100}],
              'Rice':[{l:'Small (dry)',g:60},{l:'Med (dry)',g:75},{l:'Large (dry)',g:100}],
            };
            const rawPills = [];
            // V159 FIX 6: strip any trailing "(Xg)" from servingLabel — the chip renders "{label} ({g}g)" so the weight would appear twice if label already contains it
            if (nlpModal.servingLabel && baseGrams) rawPills.push({l:nlpModal.servingLabel.replace(/\s*\(\d+(?:\.\d+)?g\)\s*$/,'').trim(),g:baseGrams});
            const cat = nlpModal.category || "";
            const catKey = Object.keys(CATEGORY_SERVINGS).find(k=>cat.toLowerCase().includes(k.toLowerCase()));
            if (catKey) CATEGORY_SERVINGS[catKey].forEach(p=>rawPills.push(p));
            if (!rawPills.find(p=>p.g===100)) rawPills.push({l:'100g',g:100});
            // Deduplicate by gram value AND normalised label
            const normLabel = label => label.toLowerCase().replace(/\(.*?\)/g,'').replace(/\d+g/g,'').trim();
            const catPills = rawPills.filter((s, index, self) => {
              const dupGrams = self.findIndex(t => t.g === s.g);
              if (dupGrams !== index) return false;
              const dupLabel = self.findIndex(t => normLabel(t.l) === normLabel(s.l));
              return dupLabel === index;
            });

            return (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
              <div style={{background:T.card,border:T.border,boxShadow:T.shadow,padding:"18px 16px",width:"100%",maxWidth:380,borderRadius:T.radius,maxHeight:"90vh",overflowY:"auto"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,paddingBottom:10,borderBottom:T.border}}>
                  <p style={{color:T.ink,fontSize:12,fontFamily:T.fontSans,fontWeight:700,margin:0,letterSpacing:"0.5px"}}>Confirm quantity</p>
                  <button onClick={()=>{setNlpModal(null);setNlpModalError("");}} style={{background:"transparent",border:"none",cursor:"pointer",color:T.textDim,display:"flex",padding:"2px"}}><X size={14} weight="bold"/></button>
                </div>
                {isBranded&&nlpModal.brand&&(
                  <div style={{display:"inline-block",background:T.accent,color:T.bg,fontSize:8,fontFamily:T.fontSans,
                    fontWeight:900,padding:"2px 6px",marginBottom:5,borderRadius:T.radiusXs||4,textTransform:"uppercase",letterSpacing:"0.5px"}}>
                    {nlpModal.brand}
                  </div>
                )}
                <p style={{color:T.text,fontSize:14,fontWeight:900,fontFamily:T.fontSans,margin:"0 0 10px",lineHeight:1.3}}>{nlpModal.name}</p>

                {/* Dynamic macro display — updates with qty/servings */}
                {noMacros ? (
                  <div style={{background:T.surface,border:T.border,padding:"10px 12px",marginBottom:12,textAlign:"center",borderRadius:T.radiusSm||6}}>
                    <p style={{margin:0,fontSize:10,fontFamily:T.fontSans,fontWeight:"bold",color:T.yellow||"#f59e0b"}}>Macro data unavailable for this item.</p>
                  </div>
                ) : (
                  <div style={{display:"flex",border:T.border,marginBottom:12,borderRadius:T.radiusSm||6,overflow:"hidden"}}>
                    {[
                      {k:"Kcal",v:calcKcal,c:"#F5A623"},
                      {k:"P",v:`${calcP}g`,c:"#E74C3C"},
                      {k:"C",v:`${calcC}g`,c:"#3498DB"},
                      {k:"F",v:`${calcF}g`,c:"#9B59B6"},
                    ].map((m,i)=>(
                      <div key={m.k} style={{flex:1,textAlign:"center",padding:"8px 2px",borderLeft:i>0?T.border:"none",background:T.surface}}>
                        <p style={{color:T.textDim,fontSize:9,fontFamily:T.fontSans,margin:0,letterSpacing:"0.5px",fontWeight:700}}>{m.k}</p>
                        <p style={{color:m.c,fontSize:16,fontWeight:900,fontFamily:T.fontSans,margin:"2px 0 0"}}>{m.v}</p>
                        <p style={{color:T.textDim,fontSize:9,fontFamily:T.fontSans,fontWeight:"bold",margin:0}}>
                          for {Math.round(totalGrams)}g
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Serving size pills — scrollable */}
                {!isBranded && catPills.length > 1 && (
                  <div style={{display:"flex",gap:6,overflowX:"auto",padding:"2px 0 10px",WebkitOverflowScrolling:"touch"}}>
                    {catPills.map((p,i)=>(
                      <button key={i} onClick={()=>{setNlpModalQty(String(p.g));setNlpServings("1");}} style={{
                        background:parseFloat(nlpModalQty)===p.g?T.tileBg:T.surfaceLight,
                        border:`1px solid ${parseFloat(nlpModalQty)===p.g?T.tileBg:T.cardBorder}`,borderRadius:24,padding:"5px 10px",cursor:"pointer",
                        whiteSpace:"nowrap",flexShrink:0,
                        color:parseFloat(nlpModalQty)===p.g?T.tileAccent:T.inkMuted,
                        fontSize:9,fontWeight:700,fontFamily:T.fontSans,
                      }}>{p.l} ({p.g}g)</button>
                    ))}
                  </div>
                )}

                {/* Cooking state selector — only if variants exist, deduplicated by label */}
                {nlpModal.cookingVariants && nlpModal.cookingVariants.length > 1 && (()=>{
                  const uniqueVariants = nlpModal.cookingVariants.filter((v, idx, self) =>
                    idx === self.findIndex(t => getVariantLabel(t.name) === getVariantLabel(v.name))
                  );
                  if (uniqueVariants.length <= 1) return null;
                  return (
                  <div style={{marginBottom:10}}>
                    <label style={{color:T.textDim,fontSize:8,fontFamily:T.fontSans,letterSpacing:"1px",display:"block",marginBottom:5,textTransform:"uppercase",fontWeight:900}}>Cooking State</label>
                    <div style={{display:"flex",gap:6,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                      {uniqueVariants.map((v,i)=>{
                        const isSelected = getVariantLabel(v.name) === getVariantLabel(nlpModal.name);
                        return (
                          <button key={i} onClick={()=>{
                            // Swap to this variant's macro values
                            setNlpModal(prev=>({...prev, name:v.name, kcal100:v.kcal100, p100:v.p100, c100:v.c100, f100:v.f100, fi100:v.fi100, su100:v.su100, na100:v.na100, grams:v.grams||prev.grams, servingLabel:v.servingLabel||prev.servingLabel, category:v.category||prev.category}));
                          }} style={{
                            background:isSelected?T.accent:T.surface,
                            border:T.border,borderRadius:24,padding:"6px 14px",cursor:"pointer",
                            whiteSpace:"nowrap",flexShrink:0,
                            color:isSelected?T.ink:T.textSub,
                            fontSize:10,fontWeight:700,fontFamily:T.fontSans,
                          }}>{getVariantLabel(v.name)}</button>
                        );
                      })}
                    </div>
                  </div>
                  );
                })()}

                {/* Gram input + servings counter */}
                <div style={{display:"flex",gap:8,alignItems:"flex-end",marginBottom:12}}>
                  <div style={{flex:1}}>
                    <label style={{color:T.inkMuted,fontSize:9,fontFamily:T.fontSans,display:"block",marginBottom:3,fontWeight:600}}>
                      {isBranded?"Serving size (g)":"Quantity (g)"}
                    </label>
                    <input type="number" inputMode="decimal" step="1" min="1"
                      value={nlpModalQty}
                      onChange={e=>{setNlpModalQty(e.target.value);setNlpModalError("");}}
                      onKeyDown={e=>{if(e.key==="Enter"){confirmModalLog(nlpServings);setNlpServings("1");}}}
                      style={{width:"100%",background:T.surface,border:T.border,boxShadow:T.shadowXs,
                        padding:"10px 12px",color:T.text,fontSize:16,fontFamily:T.fontSans,
                        fontWeight:"bold",outline:"none",boxSizing:"border-box",borderRadius:T.radiusSm||6}}/>
                  </div>
                  <div style={{width:120}}>
                    <label style={{color:T.inkMuted,fontSize:9,fontFamily:T.fontSans,display:"block",marginBottom:3,fontWeight:600}}>Servings</label>
                    <div style={{display:"flex",alignItems:"center",background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,overflow:"hidden"}}>
                      <button onClick={()=>setNlpServings(String(Math.max(1,parseFloat(nlpServings||1)-1)))}
                        style={{background:"transparent",border:"none",cursor:"pointer",padding:"10px 12px",color:T.text,fontSize:18,fontWeight:700,lineHeight:1}}>−</button>
                      <span style={{flex:1,textAlign:"center",fontSize:16,fontWeight:800,fontFamily:T.fontSans,color:T.text}}>{nlpServings}</span>
                      <button onClick={()=>setNlpServings(String(Math.min(10,parseFloat(nlpServings||1)+1)))}
                        style={{background:"transparent",border:"none",cursor:"pointer",padding:"10px 12px",color:T.text,fontSize:18,fontWeight:700,lineHeight:1}}>+</button>
                    </div>
                  </div>
                </div>

                {/* Calculated totals — prominent summary */}
                <div style={{background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,padding:"12px 14px",marginBottom:12,borderRadius:12,textAlign:"center"}}>
                  <p style={{margin:0,fontSize:22,fontWeight:800,fontFamily:T.fontSans,color:T.orange,lineHeight:1.1}}>
                    {calcKcal} <span style={{fontSize:13,fontWeight:600,color:T.inkMuted}}>kcal</span>
                  </p>
                  <p style={{margin:"6px 0 0",fontSize:13,fontFamily:T.fontSans,fontWeight:700,color:T.textSub,lineHeight:1}}>
                    P: {calcP}g · C: {calcC}g · F: {calcF}g
                  </p>
                </div>

                {nlpModalError && (
                  <p style={{color:"#dc2626",fontSize:12,fontFamily:T.fontSans,fontWeight:600,
                    margin:"0 0 10px",textAlign:"center",lineHeight:1.4}}>
                    {nlpModalError}
                  </p>
                )}
                <Btn onClick={()=>{
                  confirmModalLog(nlpServings);
                  setNlpServings("1");
                }} disabled={logLoading} color={T.orange}>
                  {logLoading?<><Spinner size={14}/> Logging...</>:<><Check size={14} weight="bold"/> Confirm Log</>}
                </Btn>
              </div>
            </div>
            );
          })()}

          {/* V27: Barcode camera UI + logErr/logOk — no wrapper card needed */}
          {/* ── BARCODE — V16.1 Camera + Manual ───────────────────────────── */}
            {/* Scanning view only shows on main page when overlay is closed */}
            {!showFoodSearch && logMode==="barcode"&&(
              <CameraErrorBoundary>
              <div>
                {/* HTTPS toast */}
                {cameraToast&&(
                  <div style={{background:T.ink,border:`3px solid ${T.yellow}`,boxShadow:T.shadow,
                    padding:"9px 13px",marginBottom:10,display:"flex",alignItems:"center",gap:9,borderRadius:T.radiusSm}}>
                    <WarningCircle size={14} weight="bold" color={T.yellow} style={{flexShrink:0}}/>
                    <p style={{color:T.yellow,fontSize:10,fontFamily:T.fontSans,fontWeight:900,margin:0,letterSpacing:"0.5px"}}>
                      {cameraToast}
                    </p>
                  </div>
                )}

                {/* Secure context error card */}
                {cameraSecureErr&&(
                  <SecureContextCard origin={window.location.origin}/>
                )}

                {/* Camera error */}
                {cameraErr&&(
                  <div style={{background:T.surface,border:`3px solid ${T.red}`,boxShadow:T.shadowXs,
                    padding:"8px 12px",marginBottom:9,display:"flex",gap:8,alignItems:"flex-start",borderRadius:T.radiusSm}}>
                    <WarningCircle size={14} weight="bold" color={T.red} style={{flexShrink:0,marginTop:1}}/>
                    <p style={{color:T.red,fontSize:10,fontFamily:T.fontSans,fontWeight:"bold",margin:0,lineHeight:1.6}}>
                      {cameraErr}
                    </p>
                  </div>
                )}

                {/* ── STATE A (IDLE) moved to FoodSearchOverlay ──────────── */}

                {/* ── STATE B: SCANNING ─────────────────────────────────────── */}
                {(barcodeStep==="scanning"||cameraActive)&&(
                  <div style={{marginBottom:10}}>
                    <div style={{position:"relative",background:T.ink,border:T.border,
                      boxShadow:T.shadow,overflow:"hidden"}}>
                      <video ref={videoRef} muted playsInline autoPlay disablePictureInPicture
                        style={{width:"100%",display:"block",maxHeight:220,objectFit:"cover"}}/>
                      {/* Tap to Start overlay — autoplay fallback */}
                      {videoReady && videoRef.current && videoRef.current.paused && (
                        <div style={{position:"absolute",inset:0,display:"flex",
                          alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.6)"}}>
                          <button onClick={()=>{
                            videoRef.current.play().catch(e => { if (process.env.NODE_ENV !== "production") { console.error(e); } });
                          }} style={{
                            background:T.secondary,border:T.border,boxShadow:T.shadow,
                            padding:"12px 22px",cursor:"pointer",color:T.ink,fontSize:13,
                            fontFamily:T.fontSans,fontWeight:900,letterSpacing:"1px",
                            textTransform:"uppercase",display:"flex",alignItems:"center",gap:8,
                          }}>
                            <Camera size={16} weight="bold"/> Tap to Start Video
                          </button>
                        </div>
                      )}
                      {/* Scan-line */}
                      <div style={{position:"absolute",top:"50%",left:"10%",right:"10%",height:2,
                        background:T.secondary,boxShadow:`0 0 8px ${T.secondary}`,opacity:0.85}}/>
                      {/* Corner markers */}
                      {[{top:8,left:8},{top:8,right:8},{bottom:8,left:8},{bottom:8,right:8}].map((pos,i)=>(
                        <div key={i} style={{position:"absolute",...pos,width:18,height:18,
                          borderTop:i<2?`3px solid ${T.secondary}`:"none",
                          borderBottom:i>=2?`3px solid ${T.secondary}`:"none",
                          borderLeft:(i===0||i===2)?`3px solid ${T.secondary}`:"none",
                          borderRight:(i===1||i===3)?`3px solid ${T.secondary}`:"none"}}/>
                      ))}
                      {/* Status badge */}
                      <div style={{position:"absolute",top:8,left:"50%",transform:"translateX(-50%)",
                        background:T.ink,border:`2px solid ${T.secondary}`,padding:"3px 9px"}}>
                        <span style={{color:T.secondary,fontSize:9,fontFamily:T.fontSans,
                          fontWeight:900,letterSpacing:"1px"}}>SCANNING…</span>
                      </div>
                    </div>
                    <button onClick={()=>{ stopCamera(); setBarcodeStep("idle"); }}
                      style={{width:"100%",background:T.surface,border:T.border,boxShadow:T.shadowXs,
                        padding:"8px 0",cursor:"pointer",color:T.red,fontSize:10,fontFamily:T.fontSans,
                        fontWeight:900,letterSpacing:"1px",textTransform:"uppercase",
                        display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginTop:6}}>
                      <X size={12} weight="bold"/> Cancel
                    </button>
                  </div>
                )}

                {/* ── STATE C: REVIEW & LOG ─────────────────────────────────── */}
                {barcodeStep==="review"&&scannedProduct&&(
                  <div>
                    {/* Product name header */}
                    <ComicCard style={{marginBottom:11}}>
                      <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:10,
                        paddingBottom:9,borderBottom:T.border}}>
                        <div style={{width:30,height:30,background:T.secondary,border:T.border,
                          display:"flex",alignItems:"center",justifyContent:"center",
                          fontSize:16,flexShrink:0}}>
                          📦
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <p style={{color:T.textDim,fontSize:8,fontFamily:T.fontSans,fontWeight:700,
                            margin:0,letterSpacing:"0.5px"}}>Product found</p>
                          <p style={{color:T.text,fontSize:13,fontFamily:T.fontSans,fontWeight:900,
                            margin:"2px 0 0",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                            {scannedProduct.name}
                          </p>
                        </div>
                      </div>

                      {/* Per-100g macro preview */}
                      <div style={{display:"flex",border:T.border,marginBottom:12}}>
                        {[
                          {label:"Kcal",  val:Math.round(scannedProduct.kcal100), color:T.accent   },
                          {label:"P",     val:`${scannedProduct.p100.toFixed(1)}g`, color:T.secondary},
                          {label:"C",     val:`${scannedProduct.c100.toFixed(1)}g`, color:T.yellow  },
                          {label:"F",     val:`${scannedProduct.f100.toFixed(1)}g`, color:T.orange  },
                        ].map((m,i)=>(
                          <div key={m.label} style={{flex:1,textAlign:"center",padding:"7px 2px",
                            borderLeft:i>0?T.border:"none"}}>
                            <p style={{color:T.textDim,fontSize:9,fontFamily:T.fontSans,margin:0,
                              letterSpacing:"0.5px",fontWeight:700}}>{m.label}</p>
                            <p style={{color:m.color,fontSize:15,fontWeight:900,
                              fontFamily:T.fontSans,margin:"2px 0 0"}}>{m.val}</p>
                            <p style={{color:T.textDim,fontSize:9,fontFamily:T.fontSans,
                              fontWeight:"bold",margin:0}}>per 100{barcodeUnit}</p>
                          </div>
                        ))}
                      </div>

                      {/* Weight / unit / servings — single row */}
                      <div style={{display:"flex",gap:7,alignItems:"flex-end",marginBottom:12}}>
                        {/* Unit toggle */}
                        <div style={{flexShrink:0}}>
                          <label style={{color:T.textDim,fontSize:9,fontFamily:T.fontSans,
                            letterSpacing:"0.5px",display:"block",marginBottom:3,
                            fontWeight:700}}>Unit</label>
                          <div style={{display:"flex",border:T.border,height:42,boxSizing:"border-box"}}>
                            {["g","ml"].map((u,i)=>(
                              <button key={u} onClick={()=>setBarcodeUnit(u)} style={{
                                padding:"0 12px",background:barcodeUnit===u?T.secondary:T.surface,
                                border:"none",borderRight:i===0?T.border:"none",
                                color:barcodeUnit===u?T.ink:T.textSub,cursor:"pointer",
                                fontSize:10,fontWeight:900,fontFamily:T.fontSans,
                                textTransform:"uppercase",letterSpacing:"0.5px",
                                height:"100%",
                              }}>{u}</button>
                            ))}
                          </div>
                        </div>
                        {/* Qty input */}
                        <div style={{flex:1}}>
                          <label style={{color:T.textDim,fontSize:9,fontFamily:T.fontSans,
                            letterSpacing:"0.5px",display:"block",marginBottom:3,
                            fontWeight:700}}>
                            Amount ({barcodeUnit})
                          </label>
                          <input type="number" value={barcodeQty}
                            onChange={e=>setBarcodeQty(e.target.value)}
                            style={{width:"100%",background:T.surface,border:T.border,
                              boxShadow:T.shadowXs,padding:"10px 9px",color:T.text,
                              fontSize:14,fontFamily:T.fontSans,fontWeight:"bold",
                              outline:"none",boxSizing:"border-box",height:42}}/>
                        </div>
                        {/* Servings */}
                        <div style={{width:70,flexShrink:0}}>
                          <label style={{color:T.textDim,fontSize:9,fontFamily:T.fontSans,
                            letterSpacing:"0.5px",display:"block",marginBottom:3,
                            fontWeight:700}}>× servings</label>
                          <input type="number" step="0.5" min="0.5" value={barcodeServings}
                            onChange={e=>setBarcodeServings(e.target.value)}
                            style={{width:"100%",background:T.surface,border:T.border,
                              boxShadow:T.shadowXs,padding:"10px 9px",color:T.text,
                              fontSize:14,fontFamily:T.fontSans,fontWeight:"bold",
                              outline:"none",boxSizing:"border-box",height:42,textAlign:"center"}}/>
                        </div>
                      </div>

                      {/* Calculated totals — prominent summary */}
                      {(()=>{
                        const qty=parseFloat(barcodeQty)||100, servMult=parseFloat(barcodeServings)||1;
                        const s=(qty/100)*servMult;
                        return (
                          <div style={{background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,padding:"12px 14px",
                            marginBottom:12,borderRadius:12,textAlign:"center"}}>
                            <p style={{margin:0,fontSize:22,fontWeight:900,fontFamily:T.fontSans,color:T.accent,lineHeight:1.1}}>
                              {Math.round(scannedProduct.kcal100*s)} <span style={{fontSize:13,fontWeight:700,color:T.textDim}}>kcal</span>
                            </p>
                            <p style={{margin:"6px 0 0",fontSize:13,fontFamily:T.fontSans,fontWeight:700,color:T.textSub,lineHeight:1}}>
                              P: {(scannedProduct.p100*s).toFixed(1)}g · C: {(scannedProduct.c100*s).toFixed(1)}g · F: {(scannedProduct.f100*s).toFixed(1)}g
                            </p>
                          </div>
                        );
                      })()}

                      {/* Action buttons */}
                      <Btn onClick={()=>{
                        confirmLog(barcodeServings);
                        setBarcodeServings("1");
                      }} disabled={logLoading} color={T.orange}
                        style={{marginBottom:7}}>
                        {logLoading
                          ? <><Spinner size={14}/> Logging…</>
                          : <><Check size={14} weight="bold"/> Add to Log</>}
                      </Btn>
                      <button onClick={()=>{ setScannedProduct(null); setBarcodeStep("idle"); setBarcode(""); }}
                        style={{width:"100%",background:"none",border:"none",cursor:"pointer",
                          color:T.textDim,fontSize:10,fontFamily:T.fontSans,fontWeight:900,
                          textTransform:"uppercase",letterSpacing:"1px",padding:"6px 0"}}>
                        ← Scan Again
                      </button>
                    </ComicCard>
                  </div>
                )}
              </div>
              </CameraErrorBoundary>
            )}
            {logErr&&<div style={{marginTop:9,background:T.surface,border:`3px solid ${T.red}`,boxShadow:T.shadowXs,padding:"7px 11px",color:T.red,fontSize:11,fontFamily:T.fontSans,fontWeight:"bold",borderRadius:T.radiusSm}}>{logErr}</div>}
            {logOk&&(
              <div style={{marginTop:9,display:"flex",gap:7,alignItems:"stretch"}}>
                <div style={{flex:1,background:T.surface,border:`3px solid ${T.accent}`,boxShadow:T.shadowXs,padding:"7px 11px",color:T.ink,fontSize:11,fontFamily:T.fontSans,fontWeight:"bold",display:"flex",gap:5,alignItems:"center",borderRadius:T.radiusSm}}>
                  <Check size={13} weight="bold" color={T.accent}/>{logOk}
                </div>
                {/* Save last logged item to favourites */}
                {logs.length>0&&(
                  <button onClick={()=>saveMeal(logs[0])} disabled={savingMeal} style={{
                    background:savingMeal?T.surface:T.yellow,border:T.border,boxShadow:T.shadowXs,
                    padding:"5px 10px",cursor:savingMeal?"default":"pointer",color:T.ink,
                    fontSize:8,fontFamily:T.fontSans,fontWeight:900,textTransform:"uppercase",
                    letterSpacing:"0.5px",flexShrink:0,display:"flex",alignItems:"center",gap:4,
                  }}>
                    {savingMeal?<Spinner size={9}/>:"⭐"} Save
                  </button>
                )}
              </div>
            )}

          {/* Copy Yesterday/All — removed from main view, moved to overflow menu (see CLAUDE-BACKLOG.md) */}
          {copyOk&&<div style={{padding:"6px 0",textAlign:"center"}}><span style={{color:T.accent,fontSize:10,fontFamily:T.fontSans,fontWeight:600}}>{copyOk}</span></div>}

          {/* Protein gap alert */}
          {proteinGapAlert&&hasTargets&&(
            <div style={{background:T.ink,border:`1px solid ${T.yellow}`,padding:"11px 16px",marginBottom:8,display:"flex",alignItems:"center",gap:10,borderRadius:18,overflow:"hidden"}}>
              <Lightning size={16} weight="bold" color={T.yellow} style={{flexShrink:0}}/>
              <div>
                <p style={{color:T.yellow,fontSize:10,fontFamily:T.fontSans,fontWeight:700,margin:0,letterSpacing:"1px",textTransform:"uppercase"}}>Protein gap alert</p>
                <p style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontSans,fontWeight:500,margin:"3px 0 0"}}>
                  {lastProteinLog
                    ? `Last high-protein meal (>15g): ${Math.round((Date.now()-new Date(lastProteinLog.logged_at).getTime())/3600000*10)/10}h ago — muscle recovery may be affected.`
                    : "No high-protein meal logged today (>15g). Try to get some protein in soon."}
                </p>
              </div>
            </div>
          )}

          {/* Food ledger — meal cards */}
          {Object.keys(groupedLogs).length>0?(
            <div style={{marginBottom:8}}>
              {Object.entries(groupedLogs).map(([mt,items])=>{
                const isOpen = !!openSlots[mt];
                const mealColor = MEAL_COLOR[mt]||T.primary;
                // Slot-level macro totals
                const slotTotals = items.reduce((a,i)=>({
                  kcal: a.kcal + (parseFloat(i.calories)||0),
                  p:    a.p    + (parseFloat(i.protein) ||0),
                  c:    a.c    + (parseFloat(i.carbs)   ||0),
                  f:    a.f    + (parseFloat(i.fat)     ||0),
                  fi:   a.fi   + (parseFloat(i.fibre)   ||0),
                  su:   a.su   + (parseFloat(i.sugar)   ||0),
                  na:   a.na   + (parseFloat(i.sodium)  ||0),
                }), {kcal:0,p:0,c:0,f:0,fi:0,su:0,na:0});

                return(
                  <div key={mt} style={{marginBottom:8,background:T.card,border:`1px solid ${T.cardBorder}`,overflow:"hidden",borderRadius:18}}>
                    {/* Meal header row — dark treatment */}
                    <button
                      onClick={()=>setOpenSlots(prev=>({...prev,[mt]:!prev[mt]}))}
                      style={{
                        width:"100%",background:T.progCardBg,border:"none",
                        padding:"12px 16px",cursor:"pointer",textAlign:"left",
                        display:"flex",alignItems:"center",gap:8,
                      }}>
                      {/* Left: dot + name + count badge */}
                      <div style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:0}}>
                        <div style={{width:10,height:10,borderRadius:5,background:mealColor,flexShrink:0}}/>
                        <span style={{fontSize:14,fontWeight:700,color:T.tileText,fontFamily:T.fontDisplay}}>{mt}</span>
                        {items.length>0&&(
                          <span style={{fontSize:10,fontWeight:600,color:"rgba(255,255,255,0.60)",background:"rgba(255,255,255,0.12)",borderRadius:8,padding:"2px 8px",marginLeft:6,fontFamily:T.fontSans}}>
                            {items.length} item{items.length!==1?"s":""}
                          </span>
                        )}
                      </div>
                      {/* Right: calorie total + macro summary + add button */}
                      <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                        <div>
                          <div style={{fontFamily:T.fontDisplay,fontSize:20,fontWeight:800,color:T.tileText,lineHeight:1,textAlign:"right",marginBottom:4}}>{Math.round(slotTotals.kcal)}</div>
                          <div style={{fontSize:10,color:"rgba(255,255,255,0.50)",fontWeight:500,fontFamily:T.fontSans,textAlign:"right",whiteSpace:"nowrap"}}>
                            {Math.round(slotTotals.p)}g pro · {Math.round(slotTotals.c)}g carb · {Math.round(slotTotals.f)}g fat
                          </div>
                        </div>
                        <div onClick={e=>{e.stopPropagation();setMealType(mt);setShowFoodSearch(true);}} style={{width:28,height:28,borderRadius:14,background:"rgba(255,255,255,0.12)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}>
                          <Plus size={14} weight="bold" color={T.tileText}/>
                        </div>
                      </div>
                    </button>

                    {/* Food item rows — always visible */}
                    {items.length>0&&(
                      <div style={{padding:"0 16px"}}>
                        {items.map(item=>{
                          const sw = swipeRef.current[item.id] || {};
                          const swiped = sw.swiped;
                          return (
                            <div key={item.id} style={{position:"relative",overflow:"hidden",borderTop:`1px solid ${T.cardBorder}`}}
                              onClick={()=>{if(swiped){swipeRef.current[item.id]={};setOpenSlots(p=>({...p}));}}}
                              onTouchStart={e=>{swipeRef.current[item.id]={startX:e.touches[0].clientX,delta:0,swiped:false};}}
                              onTouchMove={e=>{
                                const s=swipeRef.current[item.id];
                                if(!s||s.startX==null) return;
                                s.delta=s.startX-e.touches[0].clientX;
                              }}
                              onTouchEnd={()=>{
                                const s=swipeRef.current[item.id];
                                if(!s) return;
                                if(s.delta>60){s.swiped=true;setOpenSlots(p=>({...p}));}
                                else{s.swiped=false;s.delta=0;}
                              }}
                            >
                              {/* Delete button revealed behind */}
                              {swiped&&(
                                <button onClick={e=>{e.stopPropagation();deleteLog(item.id);delete swipeRef.current[item.id];}}
                                  style={{position:"absolute",right:0,top:0,bottom:0,width:70,
                                    background:T.red,border:"none",cursor:"pointer",
                                    color:"#fff",fontSize:10,fontWeight:700,fontFamily:T.fontSans,
                                    display:"flex",alignItems:"center",justifyContent:"center",gap:4,
                                    letterSpacing:"0.5px",zIndex:2}}>
                                  <Trash size={13} weight="bold"/> Delete
                                </button>
                              )}
                              <div style={{background:T.card,padding:"12px 0",
                                transform:swiped?"translateX(-70px)":"translateX(0)",
                                transition:"transform 0.2s ease",position:"relative",zIndex:1,
                                display:"flex",alignItems:"center"}}>
                                <div style={{flex:1,minWidth:0}}>
                                  <p style={{color:T.inkDark,fontSize:14,fontWeight:600,fontFamily:T.fontSans,
                                    margin:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.name}</p>
                                  <p style={{color:T.inkMuted,fontSize:12,fontWeight:500,margin:"2px 0 0",fontFamily:T.fontSans}}>
                                    {item.logged_grams ? `${item.logged_grams}g` : `${Math.round(item.calories)} kcal`}
                                  </p>
                                </div>
                                <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                                  <div style={{background:T.surfaceLight,borderRadius:8,padding:"4px 10px"}}>
                                    <span style={{fontSize:13,fontWeight:800,color:T.inkDark,fontFamily:T.fontDisplay}}>{Math.round(item.calories)}</span>
                                  </div>
                                  {/* DEF-002: Edit grams/macros button — opens full editItem modal */}
                                  <button onClick={()=>{
                                    setEditItem(item);
                                    setEditForm({
                                      grams:    String(item.logged_grams||""),
                                      name:     item.name,
                                      calories: item.calories,
                                      protein:  item.protein,
                                      carbs:    item.carbs,
                                      fat:      item.fat,
                                      fibre:    item.fibre||0,
                                      sugar:    item.sugar||0,
                                      sodium:   item.sodium||0,
                                    });
                                    fetchEditSource(item);
                                  }} style={{background:"none",border:"none",cursor:"pointer",
                                    padding:"4px 2px",display:"flex",alignItems:"center",
                                    color:T.accent,fontSize:10,fontWeight:700,fontFamily:T.fontSans}}>
                                    Edit
                                  </button>
                                  <button onClick={()=>setExpandedReassignId(prev=>prev===item.id?null:item.id)} style={{background:"none",border:"none",cursor:"pointer",
                                    color:T.inkMuted,padding:4,display:"flex"}}>
                                    <PencilSimple size={12} weight="bold"/>
                                  </button>
                                  <button onClick={()=>deleteLog(item.id)} style={{background:"none",border:"none",cursor:"pointer",color:T.inkFaint,padding:4,display:"flex"}}><X size={12} weight="bold"/></button>
                                </div>
                              </div>
                              {/* Meal-type reassign strip — hidden by default, toggled via edit button */}
                              {!swiped&&expandedReassignId===item.id&&(
                                <div style={{display:"flex",gap:4,paddingBottom:6,flexWrap:"wrap"}}>
                                  {MEALS.map(mtr=>(
                                    <button key={mtr} onClick={()=>updateMealType(item.id, mtr)}
                                      style={{
                                        padding:"2px 7px",fontSize:8,fontFamily:T.fontSans,fontWeight:700,
                                        letterSpacing:"0.3px",cursor:"pointer",borderRadius:6,
                                        border:`1px solid ${T.cardBorder}`,
                                        background: item.meal_type===mtr ? MEAL_COLOR[mtr] : "transparent",
                                        color: item.meal_type===mtr ? "#fff" : T.inkMuted,
                                      }}>
                                      {mtr}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ):(
            <div style={{textAlign:"center",padding:"32px 16px",border:`1px solid ${T.cardBorder}`,background:T.card,marginBottom:8,borderRadius:18,overflow:"hidden"}}>
              <p style={{color:T.inkDark,fontSize:20,margin:"0 0 6px",fontFamily:T.fontDisplay,fontWeight:800}}>
                Nothing logged yet
              </p>
              <p style={{color:T.inkMuted,fontSize:12,margin:0,fontFamily:T.fontSans,fontWeight:500,lineHeight:1.5}}>
                Tap Log food to start tracking.
              </p>
            </div>
          )}


          </>

        <div style={{height:8}}/>
      </div>

      {showCameraRationale&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:450,padding:20}}>
          <div style={{background:T.bg,border:T.border,boxShadow:T.shadow,width:"100%",maxWidth:380,padding:20,borderRadius:T.radius}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
              <Camera size={20} weight="bold" color={T.accent}/>
              <p style={{color:T.accent,fontSize:13,fontFamily:T.fontSans,fontWeight:900,margin:0,textTransform:"uppercase",letterSpacing:"0.5px"}}>Camera Access</p>
            </div>
            <p style={{color:T.text,fontSize:12,fontFamily:T.fontSans,fontWeight:"bold",margin:"0 0 8px",lineHeight:1.6}}>Lithe uses your camera to scan food barcodes.</p>
            <p style={{color:T.textDim,fontSize:10,fontFamily:T.fontSans,fontWeight:"bold",margin:"0 0 18px",lineHeight:1.5}}>Your camera is never used for any other purpose. No images are stored or transmitted.</p>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setShowCameraRationale(false)}
                style={{flex:1,background:T.surface,border:T.border,padding:"10px",cursor:"pointer",
                  color:T.text,fontSize:10,fontFamily:T.fontSans,fontWeight:900,textTransform:"uppercase"}}>Cancel</button>
              <button onClick={()=>{
                  try{localStorage.setItem("np_cam_rationale","1");}catch(_){}
                  setCameraRationaleShown(true); setShowCameraRationale(false);
                  setBarcodeStep("scanning"); startCameraScanner();
                }}
                style={{flex:1,background:T.accent,border:T.border,padding:"10px",cursor:"pointer",
                  color:T.ink,fontSize:10,fontFamily:T.fontSans,fontWeight:900,textTransform:"uppercase"}}>Allow Camera</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Edit Log Item Modal ──────────────────────────────────────────── */}
      {editItem&&(()=>{
        // Enhanced edit: if source food fetched, show quantity-based editor
        const srcFood = editSourceFood;
        const isBrandedEdit = srcFood?._table === 'branded_foods';
        const hasSource = !!srcFood;
        // Compute macros from grams when source available
        const editGrams = parseFloat(editForm.grams) || 100;
        const editScale = editGrams / 100;
        let editCalc = null;
        if (hasSource) {
          if (isBrandedEdit) {
            const sg = parseFloat(srcFood.serving_g) || 100;
            const bScale = editGrams / sg;
            editCalc = {
              calories: Math.round((parseFloat(srcFood.calories)||0) * bScale),
              protein:  Math.round((parseFloat(srcFood.protein)||0)  * bScale * 10)/10,
              carbs:    Math.round((parseFloat(srcFood.carbs)||0)    * bScale * 10)/10,
              fat:      Math.round((parseFloat(srcFood.fat)||0)      * bScale * 10)/10,
              fibre:    Math.round((parseFloat(srcFood.fibre)||0)    * bScale * 10)/10,
              sugar:    Math.round((parseFloat(srcFood.sugar)||0)    * bScale * 10)/10,
              sodium:   Math.round((parseFloat(srcFood.sodium)||0)   * bScale * 10)/10,
            };
          } else {
            editCalc = {
              calories: Math.round((parseFloat(srcFood.calories_per_100g)||0) * editScale),
              protein:  Math.round((parseFloat(srcFood.protein_per_100g)||0)  * editScale * 10)/10,
              carbs:    Math.round((parseFloat(srcFood.carbs_per_100g)||0)    * editScale * 10)/10,
              fat:      Math.round((parseFloat(srcFood.fat_per_100g)||0)      * editScale * 10)/10,
              fibre:    Math.round((parseFloat(srcFood.fibre_per_100g)||0)    * editScale * 10)/10,
              sugar:    Math.round((parseFloat(srcFood.sugar_per_100g)||0)    * editScale * 10)/10,
              sodium:   Math.round((parseFloat(srcFood.sodium_per_100g)||0)   * editScale * 10)/10,
            };
          }
        }
        // Serving pills for enhanced mode
        const CATEGORY_SERVINGS_EDIT = {
          'Chicken':[{l:'Small breast',g:120},{l:'Med breast',g:150},{l:'Large breast',g:200}],
          'Fish':[{l:'Small fillet',g:100},{l:'Med fillet',g:150},{l:'Large fillet',g:200}],
          'Beef':[{l:'Small',g:100},{l:'Medium',g:150},{l:'Large',g:200}],
          'Eggs':[{l:'Small egg',g:48},{l:'Medium egg',g:58},{l:'Large egg',g:68},{l:'2 eggs',g:116},{l:'3 eggs',g:174}],
          'Vegetables':[{l:'Small',g:80},{l:'Medium',g:120},{l:'Large',g:200}],
          'Fruit':[{l:'Small',g:100},{l:'Medium',g:150},{l:'Large',g:200}],
          'Bread':[{l:'1 slice',g:35},{l:'2 slices',g:70}],
          'Pasta':[{l:'Small (dry)',g:60},{l:'Med (dry)',g:80},{l:'Large (dry)',g:100}],
          'Rice':[{l:'Small (dry)',g:60},{l:'Med (dry)',g:75},{l:'Large (dry)',g:100}],
        };
        let editPills = [];
        if (hasSource) {
          const servLabel = srcFood.serving_label;
          const servG = isBrandedEdit ? parseFloat(srcFood.serving_g)||100 : parseFloat(srcFood.serving_size_g)||100;
          if (servLabel && servG) editPills.push({l:servLabel,g:servG});
          const cat = srcFood.category || "";
          const ck = Object.keys(CATEGORY_SERVINGS_EDIT).find(k=>cat.toLowerCase().includes(k.toLowerCase()));
          if (ck) CATEGORY_SERVINGS_EDIT[ck].forEach(p=>editPills.push(p));
          if (!editPills.find(p=>p.g===100)) editPills.push({l:'100g',g:100});
          // Dedup by grams
          const seen = new Set();
          editPills = editPills.filter(p=>{ if (seen.has(p.g)) return false; seen.add(p.g); return true; });
        }
        const closeEdit = () => { setEditItem(null); if(setEditSourceFood) setEditSourceFood(null); };
        const confirmEdit = async () => {
          if (hasSource && editCalc) {
            const newGrams = parseFloat(editForm.grams)||100;
            const baseName = (srcFood.name || editItem.name).replace(/\s*\(\d+g\)\s*$/,'');
            await updateLog(editItem.id, {
              calories: editCalc.calories, protein: editCalc.protein, carbs: editCalc.carbs,
              fat: editCalc.fat, fibre: editCalc.fibre, sugar: editCalc.sugar, sodium: editCalc.sodium,
              logged_grams: newGrams, name: `${baseName} (${newGrams}g)`,
            });
          } else {
            if(!editForm.name?.trim()||!editForm.calories) return;
            await updateLog(editItem.id, {
              name: editForm.name.trim(), calories: parseFloat(editForm.calories)||0,
              protein: parseFloat(editForm.protein)||0, carbs: parseFloat(editForm.carbs)||0,
              fat: parseFloat(editForm.fat)||0, fibre: parseFloat(editForm.fibre)||0,
              sugar: parseFloat(editForm.sugar)||0, sodium: parseFloat(editForm.sodium)||0,
            });
          }
          closeEdit();
        };
        return (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",
          display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,padding:20}}
          onClick={e=>{if(e.target===e.currentTarget)closeEdit();}}>
          <div style={{background:T.card,borderRadius:T.radius,border:`1.5px solid ${T.primary||T.accent}`,
            padding:20,width:"100%",maxWidth:400,maxHeight:"80vh",overflowY:"auto"}}
            onClick={e=>e.stopPropagation()}>

            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div>
                <p style={{fontSize:11,fontWeight:700,letterSpacing:"0.8px",color:T.textDim,margin:0,fontFamily:T.fontSans}}>EDIT ENTRY</p>
                <p style={{fontSize:16,fontWeight:600,color:T.text,margin:"2px 0 0",fontFamily:T.fontSans,
                  maxWidth:280,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {editItem.name}{isBrandedEdit && srcFood.brand ? ` · ${srcFood.brand}` : ''}
                </p>
              </div>
              <button onClick={closeEdit} style={{padding:8,background:T.bg,borderRadius:8,
                border:"none",cursor:"pointer",color:T.textDim,fontSize:16}}>✕</button>
            </div>

            {hasSource ? (<>
              {/* Serving size pills */}
              {editPills.length > 0 && (
                <div style={{marginBottom:16}}>
                  <p style={{fontSize:11,fontWeight:700,letterSpacing:"0.8px",color:T.textDim,margin:"0 0 8px",fontFamily:T.fontSans}}>SERVING SIZE</p>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                    {editPills.map((p,i)=>(
                      <button key={i} onClick={()=>setEditForm(f=>({...f,grams:String(p.g)}))}
                        style={{padding:"8px 14px",borderRadius:20,cursor:"pointer",fontSize:13,fontWeight:500,
                          fontFamily:T.fontSans,border:`1px solid ${String(editGrams)===String(p.g)?(T.primary||T.accent):T.border}`,
                          background:String(editGrams)===String(p.g)?(T.primary||T.accent):T.bg,
                          color:String(editGrams)===String(p.g)?'#fff':T.text}}>
                        {p.l} ({p.g}g)
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Grams input */}
              <div style={{marginBottom:16}}>
                <p style={{fontSize:11,fontWeight:700,letterSpacing:"0.8px",color:T.textDim,margin:"0 0 8px",fontFamily:T.fontSans}}>GRAMS</p>
                <input type="number" min="1" value={editForm.grams||""} onChange={e=>setEditForm(f=>({...f,grams:e.target.value}))}
                  style={{width:"100%",background:T.bg,borderRadius:T.radiusSm,border:T.border,
                    padding:12,color:T.text,fontSize:16,fontFamily:T.fontSans,fontWeight:600,
                    outline:"none",boxSizing:"border-box"}}/>
              </div>

              {/* Macro preview */}
              {editCalc && (
                <div style={{display:"flex",gap:8,marginBottom:20}}>
                  {[
                    {l:"KCAL",v:editCalc.calories,c:"#F5A623"},
                    {l:"PROTEIN",v:editCalc.protein+"g",c:"#E74C3C"},
                    {l:"CARBS",v:editCalc.carbs+"g",c:"#3498DB"},
                    {l:"FAT",v:editCalc.fat+"g",c:"#9B59B6"},
                  ].map(({l,v,c})=>(
                    <div key={l} style={{flex:1,background:T.bg,borderRadius:T.radiusSm,padding:10,textAlign:"center"}}>
                      <p style={{fontSize:10,fontWeight:700,color:T.textDim,letterSpacing:"0.6px",margin:"0 0 4px",fontFamily:T.fontSans}}>{l}</p>
                      <p style={{fontSize:15,fontWeight:700,color:c,margin:0,fontFamily:T.fontSans}}>{v}</p>
                    </div>
                  ))}
                </div>
              )}
            </>) : (<>
              {/* Basic fallback */}
              <div style={{marginBottom:12}}>
                <p style={{fontSize:11,fontWeight:700,letterSpacing:"0.8px",color:T.textDim,margin:"0 0 8px",fontFamily:T.fontSans}}>NAME</p>
                <input value={editForm.name||""} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))}
                  style={{width:"100%",background:T.bg,borderRadius:T.radiusSm,border:T.border,
                    padding:12,color:T.text,fontSize:14,fontFamily:T.fontSans,fontWeight:600,
                    outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                {[{k:"calories",l:"CALORIES (KCAL)"},{k:"protein",l:"PROTEIN (G)"},{k:"carbs",l:"CARBS (G)"},{k:"fat",l:"FAT (G)"}].map(({k,l})=>(
                  <div key={k}>
                    <p style={{fontSize:10,fontWeight:700,letterSpacing:"0.6px",color:T.textDim,margin:"0 0 4px",fontFamily:T.fontSans}}>{l}</p>
                    <input type="number" min="0" value={editForm[k]||""} onChange={e=>setEditForm(f=>({...f,[k]:e.target.value}))}
                      style={{width:"100%",background:T.bg,borderRadius:T.radiusSm,border:T.border,
                        padding:10,color:T.text,fontSize:14,fontFamily:T.fontSans,fontWeight:600,
                        outline:"none",boxSizing:"border-box"}}/>
                  </div>
                ))}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16}}>
                {[{k:"fibre",l:"FIBRE (G)"},{k:"sugar",l:"SUGAR (G)"},{k:"sodium",l:"SODIUM (MG)"}].map(({k,l})=>(
                  <div key={k}>
                    <p style={{fontSize:10,fontWeight:700,letterSpacing:"0.6px",color:T.textDim,margin:"0 0 4px",fontFamily:T.fontSans}}>{l}</p>
                    <input type="number" min="0" value={editForm[k]||""} onChange={e=>setEditForm(f=>({...f,[k]:e.target.value}))}
                      style={{width:"100%",background:T.bg,borderRadius:T.radiusSm,border:T.border,
                        padding:10,color:T.text,fontSize:13,fontFamily:T.fontSans,fontWeight:600,
                        outline:"none",boxSizing:"border-box"}}/>
                  </div>
                ))}
              </div>
            </>)}

            {/* Buttons */}
            <div style={{display:"flex",gap:10}}>
              <button onClick={closeEdit} style={{flex:1,padding:14,borderRadius:12,
                background:T.bg,border:T.border,cursor:"pointer",
                color:T.text,fontSize:15,fontWeight:600,fontFamily:T.fontSans}}>Cancel</button>
              <button onClick={confirmEdit} style={{flex:2,padding:14,borderRadius:12,
                background:T.primary||T.accent,border:"none",cursor:"pointer",
                color:"#fff",fontSize:15,fontWeight:700,fontFamily:T.fontSans}}>
                {hasSource ? 'Update Log' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      );})()}

      {/* ── Sticky Hydration Bar — pinned above bottom nav ────────── */}
      {hasTargets && (
        <div style={{
          position:"fixed",bottom:84,left:0,right:0,zIndex:500,
          backgroundColor:T.card,borderTop:`1px solid ${T.cardBorder}`,
          padding:"10px 16px",display:"flex",alignItems:"center",gap:12,
          boxShadow:T.shadowSm,
        }}>
          <span style={{fontSize:16,flexShrink:0}}>💧</span>
          <span style={{fontFamily:T.fontDisplay,fontSize:18,fontWeight:700,color:T.inkDark,lineHeight:1}}>
            {waterMl < 1000 ? `${waterMl}ml` : `${(waterMl/1000).toFixed(1)}L`}
          </span>
          <span style={{fontSize:12,color:T.inkFaint}}>/</span>
          <span style={{fontFamily:T.fontSans,fontSize:11,color:T.inkMuted}}>
            {hydrationTarget}ml
          </span>
          <div style={{flex:1,height:4,borderRadius:2,background:T.cardBorder,overflow:"hidden"}}>
            <div style={{
              height:4,borderRadius:2,
              background:T.hydrationBlue,
              width:`${Math.min(waterMl/hydrationTarget*100,100)}%`,
              transition:"width 0.3s ease",
            }}/>
          </div>
          <button onClick={()=>syncWater(-250)} style={{
            fontFamily:T.fontSans,fontSize:13,color:T.inkMuted,
            border:`1px solid ${T.cardBorder}`,borderRadius:8,
            padding:"4px 10px",background:"transparent",cursor:"pointer",flexShrink:0,
          }}>-250</button>
          <button onClick={()=>syncWater(+250)} style={{
            fontFamily:T.fontSans,fontSize:13,fontWeight:700,color:"#ffffff",
            background:T.hydrationBlue,borderRadius:8,
            padding:"4px 12px",border:"none",cursor:"pointer",flexShrink:0,
          }}>+250ml</button>
          <button onClick={()=>setShowWaterModal(true)} style={{
            width:28,height:28,borderRadius:"50%",
            background:T.card,border:`1px solid ${T.cardBorder}`,cursor:"pointer",flexShrink:0,
            display:"flex",alignItems:"center",justifyContent:"center",
            color:T.inkMuted,fontSize:18,fontWeight:700,lineHeight:1,
          }}>+</button>
        </div>
      )}

      {/* ── Custom Water Modal ──────────────────────────────────── */}
      {showWaterModal && (
        <>
          {/* Backdrop */}
          <div
            onClick={()=>setShowWaterModal(false)}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:600}}
          />
          {/* Bottom sheet */}
          <div style={{
            position:"fixed",bottom:0,left:0,right:0,zIndex:601,
            background:T.card,borderRadius:"18px 18px 0 0",
            padding:"12px 20px 36px",
          }}>
            {/* Handle */}
            <div style={{width:36,height:4,borderRadius:2,background:T.cardBorder,margin:"0 auto 16px"}}/>
            <p style={{fontFamily:T.fontDisplay,fontSize:18,fontWeight:800,color:T.inkDark,margin:"0 0 4px",textAlign:"center"}}>Add water</p>
            <p style={{fontFamily:T.fontSans,fontSize:13,color:T.inkMuted,margin:"0 0 20px",textAlign:"center"}}>Select an amount to log</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,marginBottom:20}}>
              {[
                {ml:150,label:"Small glass"},
                {ml:250,label:"Standard"},
                {ml:330,label:"Can"},
                {ml:500,label:"Bottle"},
                {ml:750,label:"Large"},
              ].map(({ml,label})=>(
                <button key={ml} onClick={()=>{syncWater(ml);setShowWaterModal(false);}} style={{
                  display:"flex",flexDirection:"column",alignItems:"center",gap:4,
                  background:T.surfaceMid,border:`1px solid ${T.cardBorder}`,
                  borderRadius:12,padding:"12px 4px",cursor:"pointer",
                }}>
                  <span style={{fontFamily:T.fontDisplay,fontSize:17,fontWeight:800,color:T.hydrationBlue}}>{ml}</span>
                  <span style={{fontFamily:T.fontSans,fontSize:10,color:T.inkMuted,textAlign:"center",lineHeight:1.2}}>{label}</span>
                </button>
              ))}
            </div>
            <button onClick={()=>setShowWaterModal(false)} style={{
              width:"100%",padding:"13px 0",borderRadius:12,
              background:"transparent",border:`1px solid ${T.cardBorder}`,
              fontFamily:T.fontSans,fontSize:15,fontWeight:600,color:T.inkMuted,cursor:"pointer",
            }}>Cancel</button>
          </div>
        </>
      )}

      {/* ── Food Search Overlay ──────────────────────────────────── */}
      <FoodSearchOverlay
        isOpen={showFoodSearch}
        onClose={()=>{setShowFoodSearch(false);if(barcodeStep!=="idle"){setBarcodeStep("idle");}if(scannedProduct){setScannedProduct(null);}if(cameraActive){stopCamera();}}}
        searchFoodsDB={searchFoodsDB}
        fetchNLP={fetchNLP}
        dbSuggestions={dbSuggestions}
        setDbSuggestions={setDbSuggestions}
        dbSearching={dbSearching}
        nlpFood={nlpFood}
        setNlpFood={setNlpFood}
        setNlpModal={setNlpModal}
        setNlpModalQty={setNlpModalQty}
        setServingPreset={setServingPreset}
        startCameraScanner={startCameraScanner}
        setBarcodeStep={setBarcodeStep}
        setLogMode={setLogMode}
        searchHistory={searchHistory}
        fetchSearchHistory={fetchSearchHistory}
        saveSearchQuery={saveSearchQuery}
        mealType={mealType}
        setMealType={setMealType}
        MEAL_COLOR={MEAL_COLOR}
        dbDebounceRef={dbDebounceRef}
        barcode={barcode}
        setBarcode={setBarcode}
        lookupProduct={lookupProduct}
        logLoading={logLoading}
        cameraErr={cameraErr}
        cameraToast={cameraToast}
        cameraSecureErr={cameraSecureErr}
        cameraRationaleShown={cameraRationaleShown}
        setShowCameraRationale={setShowCameraRationale}
        barcodeStep={barcodeStep}
        cameraActive={cameraActive}
        videoRef={videoRef}
        videoReady={videoReady}
        stopCamera={stopCamera}
        scannedProduct={scannedProduct}
        setScannedProduct={setScannedProduct}
        barcodeUnit={barcodeUnit}
        setBarcodeUnit={setBarcodeUnit}
        barcodeQty={barcodeQty}
        setBarcodeQty={setBarcodeQty}
        barcodeServings={barcodeServings}
        setBarcodeServings={setBarcodeServings}
        confirmLog={confirmLog}
        fetchYesterdayLogs={fetchYesterdayLogs}
        copyFromYesterday={copyFromYesterday}
      />

    </div></div>
  );
}
